import { CoroBanner } from "@/constants/workplaces";
import { useWorkspaceStore } from "@/_store/workspace.module";
import type { WorkspaceDetails } from "@/_store/workspaces-list.module.ts";
import { useAppsManagementModule } from "@/_store/apps-mgmt.module.ts";
import { useMyAccountStore } from "@/_store/my-account.module.ts";
import { useFiltersStore } from "@/_store/filters.module.ts";
import { useMspStore } from "@/_store/msp/msp.module.ts";
import { useAiAssistantStore } from "@/_store/ai-assistant.module.ts";
import { usePermissionsStore, type UserPermissionsResponse } from "@/_store/permissions.module.ts";
import { getActivePinia, type Pinia, type Store } from "pinia";
import { StoreModule } from "@/constants/store.ts";
import api from "@/_helpers/api";
import { axiosInstance } from "@/plugins/https";
import isEmpty from "lodash/isEmpty";
import type { AxiosRequestConfig } from "axios";

export interface ExtendedPinia extends Pinia {
  _s: Map<string, Store>;
}

export interface UseResetStore {
  all: () => void;
  allExceptIgnoreList: (ignoreList?: StoreModule[]) => void;
  [key: string]: () => void;
}

/**
 * Returns the beta component entry for a given addon or module name, if one exists.
 * Used to conditionally render `<beta-banner>` and `<beta-chip>` UI elements.
 * @deprecated Use the component's own lifecycle/feature-flag mechanism instead.
 */
export const getBetaTag = (addonOrModule: string) => {
  const workspaceStore = useWorkspaceStore();
  return (workspaceStore.workspace.betaComponents ?? []).find(
    (comp) => comp.name === addonOrModule
  );
};

/**
 * Returns `true` when the given banner should be visible (i.e. the user has not hidden it).
 * @param banner - The banner identifier to check against the workspace's hidden banners list.
 */
export const showHiddenBanner = (banner: CoroBanner) => {
  const hiddenBannersList = useWorkspaceStore().hiddenBanners || [];
  return !hiddenBannersList.includes(banner);
};

/**
 * Extracts Coro subdomain except for reserved subdomains (qa,dev,prod,beta,local).
 * @pure
 * @param hostname
 * @returns {null|string}
 */
export const extractCoroSubdomain = (hostname: string): null | string => {
  const subdomain = hostname.split(".coro.net")[0];
  const reservedSubdomains = [
    "bugatti-qa",
    "bugatti-dev",
    "secure",
    "secure-eu",
    "secure-ca",
    "beta",
    "localhost",
    "dev2",
    "major-dev-1",
    "major-dev-2",
    "minor-dev",
    "major-qa",
    "minor-qa",
    "hotfix-dev-qa",
    "major-beta",
    "minor-beta",
    "hotfix-beta",
  ];
  return reservedSubdomains.includes(subdomain) ? null : subdomain;
};

/**
 * Updates workspace store and triggers apps management handling.
 * @sideEffects — updates workspace store and apps management
 * @param workspace - The workspace details to set.
 */
export const setWorkspace = async (workspace: WorkspaceDetails): Promise<void> => {
  const workspaceStore = useWorkspaceStore();
  workspaceStore.setWorkspace(workspace);
  useAppsManagementModule().handleAppsManagement(workspace.workspaceId, false);
  await workspaceStore.getCurrentWorkspace();
};

/**
 * Resets all persisted Pinia stores. Conditionally resets or partially resets the workspace store
 * depending on whether a Coro subdomain is present in the current hostname.
 * @sideEffects — resets all persisted Pinia stores
 */
export const resetPersistedStores = () => {
  const subdomain = extractCoroSubdomain(window.location.hostname);
  useMyAccountStore().$reset();
  useFiltersStore().$reset();
  useMspStore().$reset();
  useAppsManagementModule().$reset();
  useAiAssistantStore().$reset();
  subdomain ? useWorkspaceStore().resetStateWithoutBranding() : useWorkspaceStore().$reset();
  usePermissionsStore().$reset();
};

/**
 * Makes request to workspaces and identifies if workspace is available for particular customer.
 * @sideEffects — makes HTTP request
 * @param workspaceId
 * @returns {Promise<boolean>}
 */
export const checkIfWorkspaceAvailableForCustomer = async (workspaceId: string) => {
  if (!workspaceId) {
    return false;
  }

  const userPermissionsRequest = {
    ...api.getUserPermissions(workspaceId),
  };

  try {
    const { data } = await axiosInstance.request<UserPermissionsResponse>(userPermissionsRequest);
    return !isEmpty(data?.localRole);
  } catch (e) {
    console.error(e);
    return false;
  }
};

/**
 * Mutates the AxiosRequestConfig to add a Workspace header if workspaceId is provided.
 * @pure (mutates request config)
 * @param request - The Axios request config to mutate.
 * @param workspaceId - Optional workspace ID to set as the Workspace header.
 */
export function addWorkspaceHeaderIfNeeded(request: AxiosRequestConfig, workspaceId?: string) {
  if (workspaceId) {
    request.headers = {
      Workspace: workspaceId,
    };
  }
}

/**
 * Provides a set of utility methods to reset all active Pinia stores or specific stores individually.
 * The function dynamically generates reset methods for each store within the active Pinia instance
 * and includes a method to reset all stores at once.
 *
 * @throws {Error} If there is no active Pinia instance available.
 *
 * @returns {UseResetStore} An object containing:
 * - Individual reset methods for each store (keyed by the store name).
 * - An `all` method to reset all stores at once.
 * - An `allExceptIgnoreList` method to reset all stores except some which are in ignore list
 * @example
 * // Resetting all stores
 * const resetStore = useResetStore();
 * resetStore.all();
 *
 * @example
 * // Resetting a specific store
 * const resetStore = useResetStore();
 * resetStore['tasksStore'](); // Resets the 'tasksStore'
 *
 * @example
 * // Dynamically check for a store and reset it
 * const resetStore = useResetStore();
 * if (resetStore['settingsStore']) {
 *   resetStore['settingsStore']();
 * }
 * @example
 * // Resetting all stores except those that are in default ignore list
 * const resetStore = useResetStore();
 * resetStore.allExceptIgnoreList();
 *
 * @example
 * // Resetting all stores except those that are in ignore list that you can provide
 * const resetStore = useResetStore();
 * resetStore.allExceptIgnoreList(["account"]);
 */
export const useResetStore = (): UseResetStore => {
  const pinia = getActivePinia() as ExtendedPinia;
  const defaultIgnoreList: StoreModule[] = [
    StoreModule.ACCOUNT,
    StoreModule.WEBSOCKET,
    StoreModule.MY_ACCOUNT,
    StoreModule.REDIRECTS,
    StoreModule.APPS_MGMT,
    StoreModule.WORKSPACE,
    StoreModule.PERMISSIONS,
  ];
  if (!pinia) {
    throw new Error("There is no active Pinia instance");
  }

  const resetStore: UseResetStore = {} as UseResetStore;

  pinia._s.forEach((store, name) => {
    resetStore[name] = () => store.$reset();
  });

  resetStore.all = () => pinia._s.forEach((store) => store.$reset());

  resetStore.allExceptIgnoreList = (ignoreList: StoreModule[] = defaultIgnoreList) =>
    pinia._s.forEach((store) => {
      if (!ignoreList.includes(store.$id as StoreModule)) {
        store.$reset();
      }
    });

  return resetStore;
};
